import { Injectable, inject } from '@angular/core'
import { AuctionModel } from '../../models/auction.model'
import {
    AuctionStatusUpdatePayload,
    AuctionAction,
    LotCommencementPayload,
    LotStatus,
    HubClientPacket,
    PayloadCommand,
    MessageNotificationPayload,
    MessageNotificationCodes,
    LotStatusUpdatePayload,
    BulkLotStatusUpdatePayload,
    LotOrderUpdatePayload,
    SaleyardLotCreatedPayload,
    SaleyardLotUpdatedPayload,
    LotSalePriceChangePayload,
    LotPostAuctionSaleCreatedPayload,
    OfferUpdatePayload,
    SaleyardLotMediaRemovedPayload,
    SaleyardLotMediaAddedPayload,
    AuctionCurrentLotCancelStartPayload
} from '../../../../core/models/hub-client/hub-client-packet.model'
import { AuctionService } from '../../../../shared/services/auction.service'
import { HubClientService } from '../../../../core/services/hub-client/hub-client.service'
import { UserService } from '../../../../core/services/user.service'
import { LotService } from '../../../../shared/services/lot.service'
import { AppSpinnerService } from '../../../../core/services/app-spinner.service'
import { ToastrService } from 'ngx-toastr'
import { SoundService } from '../../../../core/services/sound.service'
import { MessageService } from '../../../../core/services/message.service'
import { LotActivityStatus } from '../../../../core/constants'
import { AnnouncementType } from '../../../manage/shared/models/auction-response.model'

@Injectable()
export class AuctionHubService {
    private auctionService = inject(AuctionService)
    private hubService = inject(HubClientService)
    private lotService = inject(LotService)
    private spinner = inject(AppSpinnerService)
    private toastr = inject(ToastrService)
    private userInfo = inject(UserService)
    private soundService = inject(SoundService)
    private messageService = inject(MessageService)

    bidPacketType = 'Rtp.Bidr.Auction.Hub.Models.Payloads.LotBidPayload, Rtp.Bidr.Auction.Hub.Models'
    lotClosingPacketType = 'Rtp.Bidr.Auction.Hub.Models.Payloads.LotClosingPayload, Rtp.Bidr.Auction.Hub.Models'
    lotIncrementPacketType = 'Rtp.Bidr.Auction.Hub.Models.Payloads.LotIncrementPayload, Rtp.Bidr.Auction.Hub.Models'
    lotStartPricePacketType = 'Rtp.Bidr.Auction.Hub.Models.Payloads.LotStartPricePayload, Rtp.Bidr.Auction.Hub.Models'
    lotDetailPacketType = 'Rtp.Bidr.Auction.Hub.Models.Payloads.LotDetailPayload, Rtp.Bidr.Auction.Hub.Models'
    lotNowSellingPacketType = 'Rtp.Bidr.Auction.Hub.Models.Payloads.LotNowSellingPayload, Rtp.Bidr.Auction.Hub.Models'
    pickOfferAcceptancePacketType =
        'Rtp.Bidr.Auction.Hub.Models.Payloads.PickOfferUpdateAcceptanceStatusPayload, Rtp.Bidr.Auction.Hub.Models'
    runoutOfferAcceptancePacketType =
        'Rtp.Bidr.Auction.Hub.Models.Payloads.RunoutOfferUpdateAcceptanceStatusPayload, Rtp.Bidr.Auction.Hub.Models'
    publishHub = 'PublishAsync'
    lotConfirmNextStartLot =
        'Rtp.Bidr.Auction.Hub.Models.Payloads.AuctionCurrentLotStartPayload, Rtp.Bidr.Auction.Hub.Models'
    lotCancelConfirmNextStartLot =
        'Rtp.Bidr.Auction.Hub.Models.Payloads.AuctionCurrentLotCancelStartPayload, Rtp.Bidr.Auction.Hub.Models'
    lotCloseLot = 'Rtp.Bidr.Auction.Hub.Models.Payloads.AuctionCurrentLotClosePayload, Rtp.Bidr.Auction.Hub.Models'
    auctionOpenStart = 'Rtp.Bidr.Auction.Hub.Models.Payloads.AuctionCommencementPayload, Rtp.Bidr.Auction.Hub.Models'
    auctionFinish = 'Rtp.Bidr.Auction.Hub.Models.Payloads.AuctionFinishPayload, Rtp.Bidr.Auction.Hub.Models'

    setupHub(model: AuctionModel) {
        // Handle signals from the server
        return this.hubService.receiveAsync$.subscribe((r) => {
            switch (r.Header.Cmd) {
                case PayloadCommand.AuctionStatusUpdate: {
                    this.updateAuctionStatus(model, r)
                    break
                }
                case PayloadCommand.LotCommencement: {
                    this.setupLot(model, r)
                    break
                }
                case PayloadCommand.LotStatusUpdate: {
                    this.updateLotStatus(model, r)
                    break
                }
                case PayloadCommand.BulkLotStatusUpdate: {
                    this.updateBulkLotStatus(model, r)
                    break
                }
                case PayloadCommand.Notification: {
                    this.setupNotification(model, r)
                    break
                }
                case PayloadCommand.LotPricing: {
                    this.lotService.setupLotPricingChange(model, r)
                    break
                }
                case PayloadCommand.LotInformation: {
                    this.lotService.updateLotDetails(r.Payload, model)
                    break
                }
                case PayloadCommand.LotAutoBidConfiguration: {
                    this.lotService.setupAutoBid(model, r.Payload)
                    break
                }

                // Saleyard
                case PayloadCommand.LotCreated: {
                    this.lotService.lotCreated(model, <SaleyardLotCreatedPayload>r.Payload)
                    break
                }
                case PayloadCommand.LotUpdated: {
                    this.lotService.lotUpdated(model, <SaleyardLotUpdatedPayload>r.Payload)
                    break
                }
                case PayloadCommand.LotOrderUpdate: {
                    this.lotService.lotOrderUpdated(model, <LotOrderUpdatePayload>r.Payload)
                    break
                }
                case PayloadCommand.LotSalePriceChanged: {
                    this.lotService.updateFinishedLotPricingChange(model, <LotSalePriceChangePayload>r.Payload)
                    break
                }
                case PayloadCommand.LotPostAuctionSaleCreated: {
                    this.lotService.updateFinishedLotPostAuctionSale(model, <LotPostAuctionSaleCreatedPayload>r.Payload)
                    break
                }
                case PayloadCommand.OfferUpdate: {
                    this.lotService.updateOffer(model, <OfferUpdatePayload>r.Payload)
                    break
                }
                case PayloadCommand.LotMediaAdded: {
                    this.lotService.lotMediaAdded(model, <SaleyardLotMediaAddedPayload>r.Payload)
                    break
                }
                case PayloadCommand.LotMediaRemoved: {
                    this.lotService.lotMediaRemoved(model, <SaleyardLotMediaRemovedPayload>r.Payload)
                    break
                }
                case PayloadCommand.AuctionCurrentLotCancelStart: {
                    this.lotService.setupCancelCurrentLot(model, <AuctionCurrentLotCancelStartPayload>r.Payload)
                    break
                }
            }
        })
    }

    sendBid(model, amount) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getBidPacket(
                model.auction.id,
                model.currentLot.id,
                amount,
                model.auction.bidderAccount.id,
                userProfile.subjectId
            )
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    sendManagerBid(auctionId, currentLotId, amount, floorBidderId) {
        if (auctionId && currentLotId && amount && floorBidderId) {
            this.userInfo.loadUserProfile().subscribe((userProfile) => {
                const packet = this.getBidPacket(auctionId, currentLotId, amount, floorBidderId, userProfile.subjectId)
                this.hubService.callHubMethod(this.publishHub, packet).subscribe()
            })
        }
    }

    sendLotClosing(auctionId, lotId, lotClosing) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getLotClosingPacket(auctionId, lotId, lotClosing, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getLotClosingPacket(auctionId: number, lotId: number, lotClosing: boolean, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.LotClosing),
            Payload: {
                $type: this.lotClosingPacketType,
                LotId: lotId,
                LotClosing: lotClosing
            }
        })
    }

    sendLotNowSelling(auctionId, lotId, lotNowSelling) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getLotNowSellingPacket(auctionId, lotId, lotNowSelling, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getLotNowSellingPacket(auctionId: number, lotId: number, lotNowSelling: boolean, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.LotNowSelling),
            Payload: {
                $type: this.lotNowSellingPacketType,
                LotId: lotId,
                NowSelling: lotNowSelling
            }
        })
    }

    sendLotIncrement(auctionId, lotId, amount, alternatingBidPresetGroupId, alternatingBidIndex) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getLotIncrementPacket(
                auctionId,
                lotId,
                userProfile.subjectId,
                amount,
                alternatingBidPresetGroupId,
                alternatingBidIndex
            )
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getLotIncrementPacket(
        auctionId: number,
        lotId: number,
        userProfileId,
        amount?: number,
        alternatingBidPresetGroupId?: number,
        alternatingBidIndex?: number
    ) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.LotIncrement),
            Payload: {
                $type: this.lotIncrementPacketType,
                LotId: lotId,
                Increment: amount,
                AlternatingBidPresetGroupId: alternatingBidPresetGroupId,
                AlternatingBidIndex: alternatingBidIndex
            }
        })
    }

    sendLotStartPrice(auctionId, lotId, initialPriceType, startPrice) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getLotStartPricePacket(
                auctionId,
                lotId,
                userProfile.subjectId,
                initialPriceType,
                startPrice
            )
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getLotStartPricePacket(
        auctionId: number,
        lotId: number,
        userProfileId,
        initialPriceType?: number,
        startPrice?: number
    ) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.LotStartPrice),
            Payload: {
                $type: this.lotStartPricePacketType,
                LotId: lotId,
                InitialPriceType: initialPriceType,
                StartPrice: startPrice
            }
        })
    }

    sendLotDetail(auctionId, lotId, tally, aveWeight, totalWeight?) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getLotDetailPacket(
                auctionId,
                lotId,
                userProfile.subjectId,
                tally,
                aveWeight,
                totalWeight
            )
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getLotDetailPacket(
        auctionId: number,
        lotId: number,
        userProfileId,
        tally?: number,
        aveWeight?: number,
        totalWeight?: number
    ) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.LotDetail),
            Payload: {
                $type: this.lotDetailPacketType,
                LotId: lotId,
                Tally: tally,
                AverageWeight: aveWeight,
                TotalWeight: totalWeight
            }
        })
    }

    getBidPacket(auctionId: number, currentLotId: number, amount: number, bidderId, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.LotBid),
            Payload: {
                $type: this.bidPacketType,
                LotId: currentLotId,
                Amount: Number(amount).toFixed(2),
                BidderId: bidderId
            }
        })
    }

    sendPickOfferResult(auctionId: number, offerId: number, offerAccepted: boolean, lotIds: number[]) {
        if (auctionId && offerId && (!offerAccepted || (offerAccepted && lotIds.length))) {
            this.userInfo.loadUserProfile().subscribe((userProfile) => {
                const packet = this.getPickOfferResultPacket(
                    auctionId,
                    offerId,
                    offerAccepted,
                    lotIds,
                    userProfile.subjectId
                )
                this.hubService.callHubMethod(this.publishHub, packet).subscribe()
            })
        }
    }

    getPickOfferResultPacket(
        auctionId: number,
        offerId: number,
        offerAccepted: boolean,
        lotIds: number[],
        userProfileId
    ) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.UpdateOfferAcceptanceStatus),
            Payload: {
                $type: this.pickOfferAcceptancePacketType,
                LotIds: lotIds,
                OfferId: offerId,
                Status: offerAccepted ? 'Accepted' : 'Rejected'
            }
        })
    }

    sendRunoutOfferResult(auctionId: number, offerId: number, offerAccepted: boolean, desiredTally: number) {
        if (auctionId && offerId && (!offerAccepted || (offerAccepted && desiredTally! > 0))) {
            this.userInfo.loadUserProfile().subscribe((userProfile) => {
                const packet = this.getRunoutOfferResultPacket(
                    auctionId,
                    offerId,
                    offerAccepted,
                    desiredTally,
                    userProfile.subjectId
                )
                this.hubService.callHubMethod(this.publishHub, packet).subscribe()
            })
        }
    }

    getRunoutOfferResultPacket(
        auctionId: number,
        offerId: number,
        offerAccepted: boolean,
        desiredTally: number,
        userProfileId
    ) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.UpdateOfferAcceptanceStatus),
            Payload: {
                $type: this.runoutOfferAcceptancePacketType,
                DesiredTally: Number(desiredTally || 0),
                OfferId: offerId,
                Status: offerAccepted ? 'Accepted' : 'Rejected'
            }
        })
    }

    sendConfirmNextStartLot(auctionId, lotId) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getConfirmNextStartLot(auctionId, lotId, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getConfirmNextStartLot(auctionId: number, lotId: number, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.AuctionCurrentLotStart),
            Payload: {
                $type: this.lotConfirmNextStartLot,
                AuctionId: auctionId,
                LotId: lotId
            }
        })
    }

    sendCancelConfirmNextStartLot(auctionId, lotId) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getCancelConfirmNextStartLot(auctionId, lotId, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getCancelConfirmNextStartLot(auctionId: number, lotId: number, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.AuctionCurrentLotCancelStart),
            Payload: {
                $type: this.lotCancelConfirmNextStartLot,
                AuctionId: auctionId,
                LotId: lotId
            }
        })
    }

    sendCloseLot(auctionId, lotId) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getCloseLot(auctionId, lotId, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getCloseLot(auctionId: number, lotId: number, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.AuctionCurrentLotClose),
            Payload: {
                $type: this.lotCloseLot,
                AuctionId: auctionId,
                LotId: lotId
            }
        })
    }

    sendAuctionAction(auctionId, action) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getAuctionAction(auctionId, action, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getAuctionAction(auctionId: number, action: number, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.AuctionCommencement),
            Payload: {
                $type: this.auctionOpenStart,
                AuctionId: auctionId,
                Action: action
            }
        })
    }

    sendFinishAuction(auctionId) {
        this.userInfo.loadUserProfile().subscribe((userProfile) => {
            const packet = this.getFinishAuction(auctionId, AuctionAction.Finished, userProfile.subjectId)
            this.hubService.callHubMethod(this.publishHub, packet).subscribe()
        })
    }

    getFinishAuction(auctionId: number, action: number, userProfileId) {
        return JSON.stringify({
            Header: this.getPacketHeader(auctionId, userProfileId, PayloadCommand.AuctionFinish),
            Payload: {
                $type: this.auctionFinish,
                AuctionId: auctionId,
                Action: action
            }
        })
    }

    private getPacketHeader(auctionId: number, userProfileId: any, cmd: number) {
        return {
            AuctionId: auctionId,
            Cmd: cmd,
            UserProfileId: userProfileId,
            SentTimeUtc: new Date()
        }
    }

    updateAuctionStatus(model, signal) {
        // --- [Signal] Update auction status
        const payload = <AuctionStatusUpdatePayload>signal.Payload
        switch (payload.Status) {
            case AuctionAction.Start: {
                // ++++++ Auction started
                // Setup started auction
                this.auctionService.setupStartedAuction(model)
                break
            }
            case AuctionAction.Finished: {
                // ++++++ Auction stopped
                this.auctionService.setupFinishedAuction(model)
                break
            }
            case AuctionAction.Pause: {
                // ++++++ Auction paused
                this.auctionService.setupPausedAuction(model)
                this.lotService.setupPausedLot(model)
                break
            }
        }
    }

    setupLot(model: AuctionModel, signal: HubClientPacket) {
        // --- [Signal] Setup lot
        const payload = <LotCommencementPayload>signal.Payload
        switch (payload.Status) {
            case LotStatus.Started: {
                // ++++++ Lot started: start in
                let lot = model.currentLot
                // Check whether current lot is not set
                if (model.currentLot == null || model.currentLot.id !== payload.LotId) {
                    // Get current lot from next up
                    lot = model.nextLots.find((i) => i.id === payload.LotId)

                    // current lot media detail index - UI only
                    model.currentMediaIndex = 0
                } else if (
                    model.currentLot &&
                    model.currentLot.id === payload.LotId &&
                    model.currentLot.biddingRun !== payload.BiddingRun
                ) {
                    model.currentMediaIndex = 0
                    model.currentLot.startPrice = Number(payload.StartPrice)
                    model.currentLot.currentPrice = Number(payload.StartPrice)
                    model.currentLot.reserveStatus = payload.ReserveStatus
                    model.currentLot.initialPriceType = payload.InitialPriceType
                    model.currentLot.increment = payload.Increment
                    model.currentLot.lotClosing = payload.LotClosing
                    model.currentLot.hasUserBid = false
                }

                // Setup started lot
                if (typeof lot === 'undefined') {
                    lot = null
                }
                this.lotService.setupStartedLot(model, lot, payload.StartingIn, signal)
                break
            }
            case LotStatus.NowSelling: {
                // ++++++ Lot now selling: time remaining
                let lot = model.currentLot

                if (model.currentLot == null || model.currentLot.id !== payload.LotId) {
                    lot = model.nextLots.find((i) => i.id === payload.LotId)
                    model.currentMediaIndex = 0
                }

                if (typeof lot === 'undefined') {
                    lot = null
                }

                this.lotService.setupNowSellingLot(model, payload.TimeRemaining, signal, lot)
                break
            }
        }
    }

    updateLotStatus(model: AuctionModel, signal) {
        // --- [Signal] Update lot status
        const payload = <LotStatusUpdatePayload>signal.Payload
        switch (payload.Status) {
            case LotStatus.Started: {
                // ++++++ Lot Started
                this.lotService.updateCurrentLot(model, payload)
                break
            }
            case LotStatus.NowSelling: {
                // ++++++ Lot NowSelling: time remaining
                // Apply bid changes
                if (model.currentLot == null || model.currentLot.id !== payload.LotId) {
                    const lot = model.nextLots.find((i) => i.id === payload.LotId) || null

                    if (!lot) {
                        return
                    }

                    model.currentMediaIndex = 0
                    this.lotService.setupNowSellingLot(model, payload.TimeRemaining, null, lot)
                }

                this.lotService.setupBid(model, signal)
                break
            }
            case LotStatus.NotSold: {
                // ++++++ Lot NotSold
                this.lotService.setupNotSoldLot(model, payload)
                break
            }
            case LotStatus.Sold: {
                // ++++++ Lot Sold
                this.lotService.setupSoldLot(model, payload)
                break
            }
            case LotStatus.PassedIn: {
                // ++++++ Lot Passed In
                this.lotService.setupPassedInLot(model, payload)
                break
            }
            case LotStatus.WithdrawnNotSold: {
                // ++++++ Lot Withdrawn
                this.lotService.setupWithdrawnLot(model, payload)
                break
            }
            case LotStatus.FinishDelayed: {
                // ++++++ Lot Finishing
                this.lotService.setupBid(model, signal)
                this.lotService.setupFinishingLot(model, LotActivityStatus.FinishDelayed, model.currentLot)
                break
            }
            case LotStatus.OfferManagement: {
                // ++++++ Lot OfferManagement
                this.lotService.setupLotUnderOffer(model, payload)
                break
            }
            case LotStatus.UnderOffer: {
                // ++++++ Lot UnderOffer
                this.lotService.setupLotUnderOffer(model, payload)
                break
            }
            default:
                //there was no check if lot id match with payload
                if (model.currentLot && model.currentLot.id === payload.LotId) {
                    this.lotService.updateCurrentLot(model, payload)
                }
                break
        }
    }

    updateBulkLotStatus(model: AuctionModel, signal) {
        // --- [Signal] Update Bulk lot status
        const payload = <BulkLotStatusUpdatePayload>signal.Payload
        // We need to look through the upcoming lots, and set their statuses as
        // per the payload. If these lots are being sold, then i think we will need
        // to play the winning bid sound, and ensure the winning bidder id etc are
        // populated so the lots display as 'Sold To You' etc.
        // This will be a combination of various existing functions, but probably makes
        // sense to keep it separate so we dont interfere with the 'current' selling lot.
        if (payload && Array.isArray(payload.LotStatusUpdates)) {
            payload.LotStatusUpdates.forEach((lotStatusUpdate) => {
                this.lotService.updatePickChildLot(model, lotStatusUpdate)
            })
        }
    }

    setupNotification(model: AuctionModel, r: HubClientPacket) {
        // --- [Signal] Notification
        const payload = <MessageNotificationPayload>r.Payload
        switch (payload.Code) {
            case MessageNotificationCodes.SysError: {
                // Check whether lot is now selling
                if (model.isNowSelling()) {
                    // Allow bidding
                    model.canBid = true
                }
                // Hide spinner
                this.spinner.hide()
                // Show error message
                this.toastr.error(payload.Message, 'Error')
                break
            }
            case MessageNotificationCodes.Information: {
                if (payload.Type === AnnouncementType.Private && model.auction) {
                    if (
                        model.auction.bidderAccount &&
                        payload.RecipientAuctionBidderId === model.auction.bidderAccount.id
                    ) {
                        // ++++++ Private message
                        // this.toastr.clear(); // dont stack notifications
                        const sound = this.messageService.getMessage('auction.sounds.auctionNotification')
                        this.soundService.playSound(sound)
                        this.toastr.warning(payload.Message, payload.Title ? payload.Title : 'Private message', {
                            timeOut: 45000,
                            disableTimeOut: true,
                            closeButton: true,
                            toastClass: 'ngx-toastr notification type-' + payload.Type
                        })
                    }
                } else {
                    // ++++++ Information
                    // this.toastr.clear() // dont stack notifications
                    const sound = this.messageService.getMessage('auction.sounds.auctionNotification')
                    this.soundService.playSound(sound)
                    this.toastr.info(payload.Message, payload.Title ? payload.Title : 'Announcement', {
                        timeOut: 45000,
                        extendedTimeOut: 10000,
                        closeButton: true,
                        toastClass: 'ngx-toastr notification type-' + payload.Type
                    })
                }
                break
            }
        }
    }
}
